import BaseClient from './client';

export const PrescriptionPath = Object.freeze({
  PRESCRIPTIONS: 'v1/prescriptor/prescriptions',
  APPOINTMENTS: 'v1/prescriptor/appointments',
  SPECIAL_TEMPLATES: 'prescription-special-templates',
  ADMIN_SPECIAL_TEMPLATES: 'admin/prescription-special-templates',
  PRESCRIPTION_TEMPLATES: 'v1/prescriptor/prescription-templates',
});

const pathId = (value, name) => {
  if (value === undefined || value === null || value === '') {
    throw new TypeError(`${name} is required`);
  }
  return encodeURIComponent(String(value));
};

const requiredText = (value, name) => {
  const result = String(value ?? '').trim();
  if (!result) throw new TypeError(`${name} is required`);
  return result;
};

/**
 * Prescription API grouped by workflow:
 * prescriptions, entries, templates, special templates, and printing.
 *
 * Authentication is inherited from BaseClient and is enabled by default.
 */
export class PrescriptionService extends BaseClient {
  // -------------------------------------------------------------------------
  // Current prescription workflow (v1/prescriptor)
  // -------------------------------------------------------------------------

  createPrescription(values) {
    return this.post(PrescriptionPath.PRESCRIPTIONS, values);
  }

  /** Creates a typed prescription using the API's existing `title` field. */
  createTypedPrescription(type, values = {}) {
    return this.createPrescription({
      ...values,
      title: requiredText(type, 'type'),
    });
  }

  fetchPrescription(prescriptionId) {
    return this.get(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}`,
    );
  }

  fetchPrescriptionsByAppointment(appointmentId, query = {}) {
    return this.get(
      `${PrescriptionPath.APPOINTMENTS}/${pathId(appointmentId, 'appointmentId')}/prescriptions`,
      { query },
    );
  }

  updatePrescription(prescriptionId, values) {
    return this.put(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}`,
      values,
    );
  }

  updatePrescriptionPatientData(prescriptionId, values) {
    return this.patch(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}/patient-data`,
      values,
    );
  }

  deletePrescription(prescriptionId) {
    return this.delete(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}`,
    );
  }

  createPrescriptionEntry(prescriptionId, values) {
    return this.post(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}/entries`,
      values,
    );
  }

  updatePrescriptionEntry(prescriptionId, entryId, values) {
    return this.put(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}`
        + `/entries/${pathId(entryId, 'entryId')}`,
      values,
    );
  }

  deletePrescriptionEntry(prescriptionId, entryId) {
    return this.delete(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}`
        + `/entries/${pathId(entryId, 'entryId')}`,
    );
  }

  // -------------------------------------------------------------------------
  // Prescription templates and entry imports
  // -------------------------------------------------------------------------

  fetchTemplates(query = {}, options = {}) {
    const name = String(query?.name || '').trim();
    const path = name
      ? `${PrescriptionPath.PRESCRIPTION_TEMPLATES}/search`
      : PrescriptionPath.PRESCRIPTION_TEMPLATES;

    return this.get(path, {
      ...options,
      query: name ? { ...query, name } : query,
    });
  }

  fetchTemplate(templateId, options = {}) {
    return this.get(
      `${PrescriptionPath.PRESCRIPTION_TEMPLATES}/${pathId(templateId, 'templateId')}`,
      options,
    );
  }

  mergeTemplateEntries(prescriptionId, templateId, options = {}) {
    return this.post(
      `${PrescriptionPath.PRESCRIPTIONS}/${pathId(prescriptionId, 'prescriptionId')}`
        + '/template-entry-imports',
      { prescription_template_id: Number(templateId) },
      options,
    );
  }

  // -------------------------------------------------------------------------
  // Special prescription templates
  // -------------------------------------------------------------------------

  fetchSpecialTemplates(query = {}) {
    return this.get(PrescriptionPath.SPECIAL_TEMPLATES, { query });
  }

  createSpecialTemplate(values) {
    return this.post(`${PrescriptionPath.SPECIAL_TEMPLATES}/store`, values);
  }

  updateSpecialTemplate(values) {
    return this.post(`${PrescriptionPath.SPECIAL_TEMPLATES}/update`, values);
  }

  deleteSpecialTemplate(values) {
    return this.post(`${PrescriptionPath.SPECIAL_TEMPLATES}/delete`, values);
  }

  reorderSpecialTemplates(values) {
    return this.post(`${PrescriptionPath.SPECIAL_TEMPLATES}/update-order`, values);
  }

  downloadSpecialTemplate(templateId) {
    return this.post(
      `${PrescriptionPath.SPECIAL_TEMPLATES}/${pathId(templateId, 'templateId')}/download`,
      {},
    );
  }

  // -------------------------------------------------------------------------
  // Admin special templates
  // -------------------------------------------------------------------------

  fetchAdminSpecialTemplates(query = {}) {
    return this.get(PrescriptionPath.ADMIN_SPECIAL_TEMPLATES, { query });
  }

  createAdminSpecialTemplate(values) {
    return this.post(`${PrescriptionPath.ADMIN_SPECIAL_TEMPLATES}/store`, values);
  }

  updateAdminSpecialTemplate(values) {
    return this.post(`${PrescriptionPath.ADMIN_SPECIAL_TEMPLATES}/update`, values);
  }

  deleteAdminSpecialTemplate(values) {
    return this.post(`${PrescriptionPath.ADMIN_SPECIAL_TEMPLATES}/delete`, values);
  }

  copyAdminSpecialTemplateToDoctor(templateId, doctorId) {
    return this.post(
      `${PrescriptionPath.ADMIN_SPECIAL_TEMPLATES}/${pathId(templateId, 'templateId')}/copy`,
      { doctor_id: doctorId },
    );
  }

  // -------------------------------------------------------------------------
  // Legacy doctor templates. These mirror apiService.jsx for gradual migration.
  // -------------------------------------------------------------------------

  fetchDoctorTemplates(values = {}) {
    return this.post('doctor/getDoctorAllTemplate', values);
  }

  fetchAdminTemplates(values = {}) {
    return this.post('doctor/getAdminAllTemplate', values);
  }

  createDoctorTemplate(values) {
    return this.post('doctor/AddNewDoctorTemplate', values);
  }

  updateDoctorTemplate(values) {
    return this.post('doctor/EditNewDoctorTemplate', values);
  }

  deleteDoctorTemplate(values) {
    return this.post('doctor/DeleteDoctorNewTemplate', values);
  }

  reorderDoctorTemplates(values) {
    return this.post('doctor/UpdateTemplateOrders', values);
  }

  downloadDoctorTemplate(values) {
    return this.post('doctor/DownloadDoctorTemplate', values);
  }

  uploadDoctorTemplateAsset(values, options = {}) {
    return this.post('doctor/upload-template-asset', values, options);
  }

  fetchOtherDoctorTemplates(values = {}) {
    return this.post('doctor/getOtherDoctorTemplate', values);
  }

  createOtherDoctorTemplate(values) {
    return this.post('doctor/AddOtherDoctorTemplate', values);
  }

  updateOtherDoctorTemplate(values) {
    return this.post('doctor/EditOtherDoctorTemplate', values);
  }

  updateDoctorTemplateDetails(values) {
    return this.post('doctor/updateDoctorTempDetails', values);
  }

  // -------------------------------------------------------------------------
  // Printing, PDF generation, verification, and legacy prescription content
  // -------------------------------------------------------------------------

  printPrescription(values) {
    return this.post('v1/prescriptor/print/document-template', values);
  }

  printDocumentTemplate(values) {
    return this.post('doctor/printDocumentTemplate', values);
  }

  printReferralDocument(values) {
    return this.post('doctor/printReferDocumentTemplate', values);
  }

  generatePrescriptionPdf(values) {
    return this.post('prescriptions/v7/generate-pdf', values, {
      responseType: 'blob',
    });
  }

  verifyPrescriptionCode(values) {
    return this.withoutAuth().post('doctor/verifyPrescriptionUniqueCode', values);
  }

  createLegacyPrescriptionContent(values) {
    return this.post('doctor/addPrescriptionContent', values);
  }

  fetchLegacyPrescriptionContent(values) {
    return this.post('doctor/fetchPrescriptionContent', values);
  }

  deleteLegacyPrescriptionContent(values) {
    return this.post('doctor/deletePrescription', values);
  }
}

export const createPrescriptionService = (baseUrl, options) => (
  new PrescriptionService(baseUrl, options)
);

// Ready-to-use shared instance. Importing it does not alter any legacy caller.
export const prescriptionService = new PrescriptionService();

export default PrescriptionService;
